import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { HistoryButton } from '@/features/audit-log';
import { DataTableColumnHeader } from '@/shared/components/data-table/data-table-column-header';
import { StatusBadge } from '@/shared/components/status-badge';

import type { Region } from '../types/region.types';

/**
 * Region columns. Sortable column ids are the list's sort keys (`sortOrder` —
 * the default, the storefront store order — and `name`), sorted by the Admin
 * API; the table never sorts rows itself. The position is changed in
 * "Storefront order".
 */
export function createRegionColumns({
  canManage,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  canManage: boolean;
  onEdit: (region: Region) => void;
  onToggleActive: (region: Region) => void;
  onDelete: (region: Region) => void;
}): ColumnDef<Region, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Region" />,
      enableHiding: false,
      meta: { label: 'Region' },
      cell: ({ row }) => (
        <div className="min-w-0">
          <span className="block truncate font-medium">{row.original.name}</span>
          {row.original.arName && (
            <span className="text-muted-foreground block truncate text-[11px]" dir="rtl">
              {row.original.arName}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'code',
      header: 'Code',
      enableSorting: false,
      size: 100,
      meta: { label: 'Code' },
      cell: ({ row }) => <code className="font-mono text-[11px]">{row.original.code}</code>,
    },
    {
      id: 'status',
      header: 'Status',
      enableSorting: false,
      size: 150,
      meta: { label: 'Status' },
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          <StatusBadge variant={row.original.isActive ? 'success' : 'neutral'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </StatusBadge>
          {row.original.isSystem && <StatusBadge variant="brand">System</StatusBadge>}
        </span>
      ),
    },
    {
      id: 'brands',
      header: () => <div className="text-end">Brands</div>,
      enableSorting: false,
      size: 80,
      meta: { label: 'Brands' },
      cell: ({ row }) => <div className="text-end tabular-nums">{row.original.brandCount}</div>,
    },
    {
      id: 'denominations',
      header: () => <div className="text-end">Denominations</div>,
      enableSorting: false,
      size: 140,
      meta: { label: 'Denominations' },
      cell: ({ row }) => (
        <div className="text-end tabular-nums">
          {row.original.activeDenominationCount}
          <span className="text-muted-foreground"> / {row.original.denominationCount} active</span>
        </div>
      ),
    },
    {
      accessorKey: 'sortOrder',
      // Position 1 first: TanStack would start a numeric column descending.
      sortDescFirst: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Storefront position" align="end" />
      ),
      size: 120,
      meta: { label: 'Storefront position' },
      cell: ({ row }) => <div className="text-end tabular-nums">{row.original.sortOrder}</div>,
    },
    {
      id: 'actions',
      header: '',
      size: 80,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <span className="flex justify-end gap-1">
          <HistoryButton
            entityType="Region"
            entityId={String(row.original.id)}
            name={row.original.name ?? `region ${row.original.id}`}
          />
          {canManage && (
            <RegionRowActions
              region={row.original}
              onEdit={onEdit}
              onToggleActive={onToggleActive}
              onDelete={onDelete}
            />
          )}
        </span>
      ),
    },
  ];
}

function RegionRowActions({
  region,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  region: Region;
  onEdit: (region: Region) => void;
  onToggleActive: (region: Region) => void;
  onDelete: (region: Region) => void;
}) {
  // Only an unused region can be deleted; the Admin API re-checks at delete time.
  const inUse = region.denominationCount > 0;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Actions for ${region.name}`}
        >
          <MoreHorizontal className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onSelect={() => onEdit(region)} className="gap-2 text-xs">
          <Pencil className="size-3.5" aria-hidden />
          Edit region
        </DropdownMenuItem>
        {/* The system region's status is fixed and it is never deleted; the Admin API refuses both too. */}
        {!region.isSystem && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onToggleActive(region)}
              className="gap-2 text-xs"
              variant={region.isActive ? 'destructive' : 'default'}
            >
              {region.isActive ? (
                <>
                  <PowerOff className="size-3.5" aria-hidden />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="size-3.5" aria-hidden />
                  Activate
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => onDelete(region)}
              disabled={inUse}
              className="gap-2 text-xs"
              variant="destructive"
            >
              <Trash2 className="size-3.5" aria-hidden />
              {inUse ? (
                <span className="flex flex-col">
                  Delete
                  <span className="text-muted-foreground text-[11px]">
                    In use by {region.denominationCount} denomination
                    {region.denominationCount === 1 ? '' : 's'}
                  </span>
                </span>
              ) : (
                'Delete'
              )}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
